import React, { useState } from 'react';
import { gql } from '@apollo/client';
import { DocumentTextIcon, TrashIcon, ArrowDownTrayIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import client from '../../lib/apolloClient';

const UPLOAD_PROGRAM_RESOURCE = gql`
  mutation UploadProgramResource($sprint_program_id: ID!, $file: File!, $name: String, $category: String) {
    upload_program_resource(sprint_program_id: $sprint_program_id, file: $file, name: $name, category: $category) {
      document {
        id
        name
        download_url
        created_at
        uploaded_by {
          name
        }
      }
      errors
    }
  }
`;

const DELETE_ENROLLMENT_FILE = gql`
  mutation DeleteEnrollmentFile($document_id: ID!, $sprint_program_id: ID) {
    delete_enrollment_file(document_id: $document_id, sprint_program_id: $sprint_program_id) {
      success
      errors
    }
  }
`;

const ProgramResourcesUpload = ({ program, enrollmentCount, onRefetch }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Capture the input element before async operations (React reuses synthetic events)
    const inputElement = e.target;

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setError(`File size exceeds 10MB limit. File size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const result = await client.mutate({
        mutation: UPLOAD_PROGRAM_RESOURCE,
        variables: {
          sprint_program_id: program.id,
          file: [file],
          name: file.name,
          category: 'other',
        },
        context: { hasUpload: true },
      });

      if (result.data?.upload_program_resource?.errors?.length > 0) {
        throw new Error(result.data.upload_program_resource.errors.join(', '));
      }

      // Reset file input using captured element
      if (inputElement) {
        inputElement.value = '';
      }

      // Refetch program data
      if (onRefetch) {
        onRefetch();
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this resource? All enrolled participants will lose access to it.')) {
      return;
    }

    try {
      setDeletingId(documentId);
      setError(null);

      const result = await client.mutate({
        mutation: DELETE_ENROLLMENT_FILE,
        variables: {
          document_id: documentId,
          sprint_program_id: program.id,
        },
      });

      if (result.data?.delete_enrollment_file?.errors?.length > 0) {
        throw new Error(result.data.delete_enrollment_file.errors.join(', '));
      }

      // Refetch program data
      if (onRefetch) {
        onRefetch();
      }
    } catch (err) {
      console.error('Error deleting file:', err);
      setError(err.message || 'Failed to delete file');
      setDeletingId(null);
    }
  };

  const documents = program?.documents || [];

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-950 p-4">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Warning */}
      {enrollmentCount > 0 && (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 dark:bg-yellow-950 p-4">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Resources are visible to all enrolled participants
              </p>
              <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                {enrollmentCount} participant{enrollmentCount !== 1 ? 's' : ''} currently enrolled
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Upload Section */}
      <div className="rounded-lg border border-succession-border bg-succession-bg-primary p-4">
        <label className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border border-succession-border rounded-lg cursor-pointer text-succession-text-primary hover:bg-succession-hover-bg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          <DocumentTextIcon className="w-4 h-4" />
          {uploading ? 'Uploading...' : 'Upload Resource'}
          <input
            type="file"
            className="hidden"
            onChange={handleFileUpload}
            disabled={uploading}
          />
        </label>
        <p className="text-xs text-succession-text-secondary mt-2">
          Maximum file size: 10MB. Resources will be visible to all enrolled participants.
        </p>
      </div>

      {/* File List */}
      {documents.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-succession-text-primary mb-3">
            Program Resources ({documents.length})
          </h3>
          <div className="space-y-2">
            {documents.map((file) => (
              <div key={file.id} className="flex items-center justify-between p-3 bg-succession-bg-secondary rounded-lg border border-succession-border hover:bg-succession-hover-bg transition-colors">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <DocumentTextIcon className="w-5 h-5 text-succession-text-secondary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-succession-text-primary truncate">
                      {file.name || 'Unnamed file'}
                    </div>
                    <div className="text-xs text-succession-text-secondary">
                      {file.uploaded_by?.name && `Uploaded by ${file.uploaded_by.name}`}
                      {file.created_at && ` • ${formatDate(file.created_at)}`}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {file.download_url && (
                    <a
                      href={file.download_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-succession-text-secondary hover:text-succession-primary transition-colors"
                      title="Download"
                    >
                      <ArrowDownTrayIcon className="w-4 h-4" />
                    </a>
                  )}
                  {file.can_delete && (
                    <button
                      onClick={() => handleDelete(file.id)}
                      disabled={deletingId === file.id}
                      className="p-2 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Delete"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {documents.length === 0 && (
        <div className="rounded-lg border border-succession-border bg-succession-bg-primary p-12 text-center">
          <DocumentTextIcon className="w-12 h-12 mx-auto mb-4 text-succession-text-secondary opacity-50" />
          <p className="text-sm text-succession-text-secondary">
            No resources uploaded yet. Upload your first resource above.
          </p>
        </div>
      )}
    </div>
  );
};

export default ProgramResourcesUpload;

